import { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Bell, Menu, LogOut, User, Settings, Building2, Check, ChevronRight, Search } from 'lucide-react'
import { api, type Membership } from '@/lib/api'
import { getInitials } from '@/lib/utils'
import { seatLabel } from '@/lib/agent-seats'
import { useAuth } from '@/contexts/AuthContext'
import NotificationPanel from '@/components/notifications/NotificationPanel'
import FeedbackReporter from '@/components/feedback/FeedbackReporter'
import { OPEN_COMMAND_PALETTE_EVENT } from '@/lib/nav'
import type { User as UserType, Notification } from '@/types'

interface HeaderProps {
  onMenuToggle: () => void
}

const routeTitles: Record<string, string> = {
  '/home': 'Home',
  '/find': 'Find Leads',
  '/eval': 'Evaluate',
  '/opportunities': 'Opportunities',
  '/buyers': 'Buyers',
  '/agents': 'Agents',
  '/wholesalers': 'Wholesalers',
  '/ops': 'Ops',
  '/integrations': 'Integrations',
  '/data-sources': 'Data Sources',
  '/settings': 'Settings',
}

function getPageTitle(pathname: string): string {
  if (routeTitles[pathname]) return routeTitles[pathname]
  if (pathname.startsWith('/find/')) return 'List Detail'
  if (pathname.startsWith('/eval/')) return 'Property Evaluation'
  if (pathname.startsWith('/opportunities/')) return 'Opportunity Detail'
  if (pathname === '/ops') return 'Ops'
  if (pathname.startsWith('/agents/')) return seatLabel(decodeURIComponent(pathname.split('/')[2] ?? ''))
  if (pathname.startsWith('/chat')) return 'Team'
  return 'Forge'
}

export default function Header({ onMenuToggle }: HeaderProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { signOut } = useAuth()
  const [notifOpen, setNotifOpen] = useState(false)
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false)
  const [user, setUser] = useState<UserType | null>(null)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [memberships, setMemberships] = useState<Membership[]>([])
  const [orgPickerOpen, setOrgPickerOpen] = useState(false)
  const [switchingOrgId, setSwitchingOrgId] = useState<string | null>(null)
  const notifRef = useRef<HTMLDivElement>(null)
  const avatarRef = useRef<HTMLDivElement>(null)

  const pageTitle = getPageTitle(location.pathname)
  const currentOrgId = useMemo(() => api.getTokenPayload()?.orgId ?? null, [user])

  useEffect(() => {
    api.getMe()
      .then((result) => {
        if (result?.user) setUser(result.user as unknown as UserType)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    api.getMemberships()
      .then((result) => setMemberships(result?.memberships ?? []))
      .catch(() => setMemberships([]))
  }, [])

  const handleSwitchOrg = useCallback(async (orgId: string) => {
    if (orgId === currentOrgId || switchingOrgId) return
    setSwitchingOrgId(orgId)
    try {
      await api.switchOrg(orgId)
      window.location.href = '/'
    } catch {
      setSwitchingOrgId(null)
    }
  }, [currentOrgId, switchingOrgId])

  useEffect(() => {
    api.getNotifications()
      .then((result) => setNotifications((result as Notification[]) || []))
      .catch(() => {})
  }, [])

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  )

  const closeNotif = useCallback(() => setNotifOpen(false), [])
  const closeAvatar = useCallback(() => { setAvatarMenuOpen(false); setOrgPickerOpen(false) }, [])

  useEffect(() => {
    if (!avatarMenuOpen) return
    function handleClick(e: MouseEvent) {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) closeAvatar()
    }
    function handleKey(e: KeyboardEvent) { if (e.key === 'Escape') closeAvatar() }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('keydown', handleKey) }
  }, [avatarMenuOpen, closeAvatar])

  const currentMembership = useMemo(
    () => memberships.find((m) => m.orgId === currentOrgId) ?? null,
    [memberships, currentOrgId]
  )
  const hasMultipleOrgs = memberships.length > 1

  return (
    <header className={`sticky top-0 z-30 shrink-0 border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-sm pt-[env(safe-area-inset-top)] ${location.pathname === '/chat' ? 'hidden md:block' : ''}`}>
      <div className="flex items-center justify-between h-14 px-4 md:px-6">
        {/* Left: mobile menu + page title */}
        <div className="flex items-center gap-3">
          <button
            onClick={onMenuToggle}
            className="flex md:hidden items-center justify-center w-8 h-8 rounded-lg text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50 transition-all duration-200"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-semibold text-zinc-50">{pageTitle}</h1>
        </div>

        {/* Right: search, report, notifications, avatar (chat lives in
            the floating bottom-right launcher — see ChatLauncher). */}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event(OPEN_COMMAND_PALETTE_EVENT))}
            className="flex items-center gap-2 h-8 rounded-lg text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50 transition-all duration-200 w-8 justify-center md:w-auto md:justify-start md:px-2.5 md:border md:border-zinc-800 md:bg-zinc-900/60 md:mr-1"
            aria-label="Search"
            title="Search and jump to (⌘K)"
          >
            <Search className="w-4 h-4" />
            <span className="hidden md:inline text-xs text-zinc-500">Search</span>
            <kbd className="hidden md:inline text-[10px] font-sans text-zinc-500 border border-zinc-700 rounded px-1 ml-3">⌘K</kbd>
          </button>

          <FeedbackReporter />

          {/* Notification bell */}
          <div ref={notifRef} className="relative">
            <button
              onClick={() => setNotifOpen((prev) => !prev)}
              className="relative flex items-center justify-center w-8 h-8 rounded-lg text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50 transition-all duration-200"
              aria-label="Notifications"
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                  {unreadCount}
                </span>
              )}
            </button>

            <NotificationPanel
              open={notifOpen}
              onClose={closeNotif}
              anchorRef={notifRef}
            />
          </div>

          {/* User avatar dropdown */}
          <div ref={avatarRef} className="relative">
            <button
              onClick={() => setAvatarMenuOpen((o) => !o)}
              className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-500 text-zinc-950 text-xs font-semibold cursor-pointer hover:ring-2 hover:ring-brand-400/40 transition-all duration-200"
              aria-label="User menu"
            >
              {getInitials(user?.name ?? 'U')}
            </button>

            {avatarMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-64 rounded-xl border border-zinc-800 bg-zinc-900 shadow-xl py-1 z-50">
                {user && (
                <div className="px-3 py-2 border-b border-zinc-800">
                  <p className="text-xs font-medium text-zinc-300">{user.name}</p>
                  <p className="text-xs text-zinc-500">{user.email}</p>
                </div>
                )}

                {currentMembership && (
                  <div className="px-3 py-2 border-b border-zinc-800">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Current org</p>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-3.5 h-3.5 text-brand-400 shrink-0" />
                      <p className="text-xs font-medium text-zinc-200 truncate">{currentMembership.orgName}</p>
                    </div>
                    <p className="text-[10px] text-zinc-500 mt-0.5 capitalize">
                      {currentMembership.role} · {currentMembership.plan}
                      {!currentMembership.activated && ' · pending activation'}
                    </p>
                  </div>
                )}

                {hasMultipleOrgs && (
                  <button
                    onClick={() => setOrgPickerOpen((o) => !o)}
                    className="flex items-center justify-between w-full px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800/50 hover:text-zinc-100 transition-all duration-200"
                  >
                    <span className="flex items-center gap-2.5">
                      <Building2 className="w-4 h-4" />
                      Switch organization
                    </span>
                    <ChevronRight className={`w-3.5 h-3.5 transition-transform ${orgPickerOpen ? 'rotate-90' : ''}`} />
                  </button>
                )}

                {hasMultipleOrgs && orgPickerOpen && (
                  <div className="bg-zinc-950/40 border-y border-zinc-800 max-h-64 overflow-y-auto">
                    {memberships.map((m) => {
                      const isCurrent = m.orgId === currentOrgId
                      const isSwitching = switchingOrgId === m.orgId
                      return (
                        <button
                          key={m.orgId}
                          onClick={() => handleSwitchOrg(m.orgId)}
                          disabled={isCurrent || !!switchingOrgId}
                          className={`flex items-start justify-between gap-2 w-full px-3 py-2 text-left transition-all duration-200 ${
                            isCurrent
                              ? 'bg-brand-500/5 cursor-default'
                              : 'hover:bg-zinc-800/50 cursor-pointer'
                          } ${switchingOrgId && !isSwitching ? 'opacity-40' : ''}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className={`text-xs font-medium truncate ${isCurrent ? 'text-brand-300' : 'text-zinc-200'}`}>
                              {m.orgName}
                            </p>
                            <p className="text-[10px] text-zinc-500 capitalize mt-0.5">
                              {m.role} · {m.plan}
                              {!m.activated && ' · pending'}
                            </p>
                          </div>
                          {isCurrent && <Check className="w-3.5 h-3.5 text-brand-400 shrink-0 mt-0.5" />}
                          {isSwitching && (
                            <span className="text-[10px] text-zinc-400 shrink-0 mt-0.5">switching…</span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                )}

                <button
                  onClick={() => { navigate('/settings'); setAvatarMenuOpen(false) }}
                  className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800/50 hover:text-zinc-100 transition-all duration-200"
                >
                  <User className="w-4 h-4" />
                  Profile
                </button>
                <button
                  onClick={() => { navigate('/settings'); setAvatarMenuOpen(false) }}
                  className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800/50 hover:text-zinc-100 transition-all duration-200"
                >
                  <Settings className="w-4 h-4" />
                  Settings
                </button>
                <div className="my-1 border-t border-zinc-800" />
                <button
                  onClick={() => { setAvatarMenuOpen(false); signOut() }}
                  className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-red-400 hover:bg-zinc-800/50 hover:text-red-300 transition-all duration-200"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
